/* eslint-disable react/jsx-filename-extension */
import type React from 'react';
import { PureComponent } from 'react';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import qs from 'qs';

import type { SaleTimes } from 'Types/saleTimes';
import { isEmptyObject, isProd } from 'Utilities/helpers';

export interface TimeLeft {
	days: number;
	hours: number;
	millisecondsRemaining: SaleTimes['millisecondsRemaining'];
	minutes: number;
	seconds: number;
}

type CountdownTime = Nullable<string> | Nullable<Dayjs>;

export const calculateTimeDifference = (
	countdownTime: CountdownTime = dayjs(new Date()),
	timeDifference = 0,
): boolean | TimeLeft => {
	const now = dayjs().subtract(timeDifference, 'seconds');
	const countdown = dayjs(countdownTime);
	const difference = countdown.diff(now);
	if (difference >= 1000) {
		return {
			days: countdown.diff(now, 'days'),
			hours: countdown.diff(now, 'hours') % 24,
			millisecondsRemaining: difference,
			minutes: countdown.diff(now, 'minutes') % 60,
			seconds: countdown.diff(now, 'seconds') % 60,
		};
	}

	return false;
};

/**
 * Format timeLeft object into a time string.
 * @param {Object} timeLeft
 * @return {String} Time in formatted string. eg 1d 3h 12m 30s.
 */
export const renderTimeString = (timeLeft: TimeLeft): string => {
	if (isEmptyObject(timeLeft) || !timeLeft) {
		return '';
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { millisecondsRemaining, ...relevantTimeLeft } = timeLeft;
	const times = Object.entries(relevantTimeLeft);

	return times
		.map(([key], index) => {
			const prevTimeSection = index === 0 ? 0 : index - 1;
			// eg If 0h and 0m, don't show minutes. If 1h 0m, show minutes. Always display seconds.
			if (times[prevTimeSection][1] === 0 && timeLeft[key as keyof TimeLeft] === 0 && key !== 'seconds') {
				return '';
			}
			return `${timeLeft[key as keyof TimeLeft]}${key[0]}`;
		})
		.join(' ');
};

// Use this param to change countdown ?setTime=2020-02-20T14:00:00.000Z.
export const getCountdownFromUrl = (): Nullable<Dayjs> => {
	const isSSR = !process.browser;
	if (isProd) {
		return null;
	}
	if (isSSR) {
		return null;
	}
	const queryParams = qs.parse(window.location.href.split('?')[1]);
	return queryParams.setTime ? dayjs(queryParams.setTime as string) : null;
};

type ChildrenProps = {
	timeLeft: boolean | TimeLeft;
	timeString: string;
};

interface TimeCountdownProps {
	children: (props: ChildrenProps) => React.ReactNode;
	countdownTime: CountdownTime;
	ignoreCountDownEnd?: boolean;
	renderTimeString?: (timeLeft: TimeLeft) => string;
	timeDifference: number;
	whenCountdownEnds: () => void;
}

interface TimeCountdownState {
	countdownHasStarted: boolean;
	countdownTime: CountdownTime;
	timeDifference: number;
	timeLeft: boolean | TimeLeft;
}

class TimeCountdown extends PureComponent<TimeCountdownProps, TimeCountdownState> {
	timerInterval?: NodeJS.Timeout;

	constructor(props: TimeCountdownProps) {
		super(props);
		const { countdownTime = dayjs(), timeDifference = 0 } = props;
		this.state = {
			countdownHasStarted: false,
			countdownTime: getCountdownFromUrl() || dayjs(countdownTime).add(1, 'second'),
			timeDifference,
			timeLeft: calculateTimeDifference(countdownTime, timeDifference),
		};
	}

	componentDidMount(): void {
		this.setInterval();
	}

	componentDidUpdate(prevProps: TimeCountdownProps, prevState: TimeCountdownState): void {
		const { countdownHasStarted, timeLeft } = this.state;
		const { countdownTime, ignoreCountDownEnd = false } = this.props;
		const { countdownTime: prevCountdownTime } = prevProps;

		if (prevState.countdownHasStarted && !countdownHasStarted && !ignoreCountDownEnd) {
			this.countdownHasEnded();
		}

		if (!timeLeft) {
			clearInterval(this.timerInterval);
		}

		if (prevCountdownTime !== countdownTime) {
			this.updateCountDownTime(countdownTime);
		}
	}

	componentWillUnmount(): void {
		clearInterval(this.timerInterval);
	}

	setInterval = (): void => {
		const { timeDifference } = this.state;

		clearInterval(this.timerInterval);

		this.timerInterval = setInterval(
			() =>
				this.setState((prevState) => {
					const timeLeft = calculateTimeDifference(prevState.countdownTime, timeDifference);
					return {
						countdownHasStarted: timeLeft !== false,
						timeLeft,
					};
				}),
			1000,
		);
	};

	countdownHasEnded = (): void => {
		const { whenCountdownEnds } = this.props;
		this.setState({ countdownHasStarted: false });
		whenCountdownEnds?.();
	};

	updateCountDownTime = (countdownTime: CountdownTime, timeDifference = 0): void => {
		this.setState({
			countdownHasStarted: false,
			countdownTime,
			timeDifference,
			timeLeft: calculateTimeDifference(countdownTime, timeDifference),
		});
		this.setInterval();
	};

	render(): React.ReactNode {
		const { children, renderTimeString: renderTime = renderTimeString } = this.props;
		const { timeLeft } = this.state;

		return children({ timeLeft, timeString: renderTime(timeLeft as TimeLeft) });
	}
}

export default TimeCountdown;
